/**
 * Recording Query Keys
 *
 * Recordings are a top-level resource (LF-3713 / LF-3712 epic). The keys for
 * detail and preview drop the matter segment — `recordingId` alone is the
 * canonical identifier. The matter-tab list keeps `matterId` because it's
 * the actual filter the FE caches the result by.
 */

import type { RecordingFilters, ScribeListFilters } from './types';

/**
 * @example
 * ```typescript
 * // Invalidate all recordings for a matter (list view)
 * queryClient.invalidateQueries({ queryKey: recordingKeys.list(matterId) })
 *
 * // Get recording detail with polling
 * useQuery({
 *   queryKey: recordingKeys.detail(recordingId),
 *   queryFn: ...,
 *   refetchInterval: 5000,
 * })
 * ```
 */
export const recordingKeys = {
  /** Root key for all recording queries */
  all: ['recordings'] as const,

  /** Base key for recording lists */
  lists: () => [...recordingKeys.all, 'list'] as const,

  /**
   * Matter-scoped recording list (matter-tab view)
   * @param matterId - The matter ID
   * @param filters - Optional pagination and mode filters
   */
  list: (matterId: number, filters?: RecordingFilters) =>
    [
      ...recordingKeys.lists(),
      matterId,
      ...(filters ? [filters] : [])
    ] as const,

  /**
   * Lead-scoped recording list (`GET /recordings?leadId=`) — a pre-matter
   * enquiry's own recordings (LF-5995).
   *
   * **Nested under `lists()` on purpose.** Every binding mutation in
   * `recordings/binding.ts` invalidates `recordingKeys.lists()`, so a bind
   * refreshes this entry without the mutation having to know which lead the
   * recording came from.
   */
  leadList: (leadId: number) =>
    [...recordingKeys.lists(), 'lead', leadId] as const,

  /**
   * Firm-wide / matter-scoped Scribe list (LF-3712 top-level surface).
   * Keyed by the full filter shape — search query, status set, archived flag,
   * and optional matter scope — so each distinct view caches independently.
   */
  scribeList: (filters: ScribeListFilters) =>
    [...recordingKeys.lists(), 'scribe', filters] as const,

  /** Base key for recording details */
  details: () => [...recordingKeys.all, 'detail'] as const,

  /**
   * Full recording detail (includes transcript, AI summary)
   * @param recordingId - The recording ID
   */
  detail: (recordingId: number) =>
    [...recordingKeys.details(), recordingId] as const,

  /** Base key for preview-changes queries */
  previews: () => [...recordingKeys.all, 'preview'] as const,

  /**
   * Preview extracted entities for import.
   * @param recordingId - The recording ID
   *
   * Live diff against current matter state — only fetched for `ready`
   * recordings. The historical post-import view is served from the
   * recording detail's `appliedPreviewSnapshot` (LF-3545).
   */
  preview: (recordingId: number) =>
    [...recordingKeys.previews(), recordingId] as const
} as const;
